/**
 * Store tema mojito-light / mojito-dark.
 *
 * Applica due meccanismi (vedi assets/app.css):
 *   - `data-theme` su <html>  → tema DaisyUI custom
 *   - classe `.dark` su <html> → variant `dark:` di Tailwind (usato per
 *                                utility ortogonali a DaisyUI, es. text-mojito-300)
 *
 * `readStoredTheme`/`applyTheme` sono esportati a parte così che `main.js`
 * possa applicare il tema prima del primo paint (no FOUC) senza Pinia.
 *
 * Migrazione one-shot: i valori legacy 'light'/'dark' (pre-step1) vengono
 * riscritti su `mojito-light`/`mojito-dark` al primo read.
 */

import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

const STORAGE_KEY = 'mojito-theme'
const VALID = new Set(['mojito-light', 'mojito-dark'])

// Colore della status bar (Android/PWA) = sfondo app (base-100), per tema.
// Deve combaciare con --color-base-100 in assets/app.css.
const THEME_COLOR = {
  'mojito-light': '#ffffff',
  'mojito-dark': '#0b1220'
}

export function readStoredTheme () {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (raw === 'dark' || raw === 'light') {
    const migrated = raw === 'dark' ? 'mojito-dark' : 'mojito-light'
    localStorage.setItem(STORAGE_KEY, migrated)
    return migrated
  }
  return VALID.has(raw) ? raw : 'mojito-light'
}

export function applyTheme (theme) {
  const root = document.documentElement
  root.dataset.theme = theme
  root.classList.toggle('dark', theme === 'mojito-dark')
  // Status bar (Android standalone / barra indirizzi) segue il tema.
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', THEME_COLOR[theme] ?? THEME_COLOR['mojito-light'])
}

export const useThemeStore = defineStore('theme', () => {
  const theme = ref(readStoredTheme())

  watch(theme, (value) => {
    localStorage.setItem(STORAGE_KEY, value)
    applyTheme(value)
  })

  function toggle () {
    theme.value = theme.value === 'mojito-dark' ? 'mojito-light' : 'mojito-dark'
  }

  return { theme, toggle }
})
